import { useReturnNavigation } from "../../../app/navigation/return-navigation.js";
import { BackLink } from "../../../app/components/BackLink.js";
import type { PassportCommand } from "@peer-start/platform-contracts";
import { Button, Card, StatusBadge, PageHeading, PageColumns, CardHeader } from "@peer-start/ui";
import { BadgeCheck, CheckCircle2, Clock3 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { useAppContext } from "../../../app/use-app-context.js";
import { AssessmentPassportCard } from "../../assessment/components/AssessmentPassportCard.js";
import { usePassportCommand, useStampDetail } from "../api/queries.js";
import { EvidenceFacets } from "../components/EvidenceFacets.js";
import { PassportQueryState } from "../components/PassportQueryState.js";
import { PracticeEvidenceForm } from "../components/PracticeEvidenceForm.js";
import { StampAdvancedMatrix, StampAtomicCoverage } from "../components/StampAtomicCoverage.js";
import { StampEvidenceHistory } from "../components/StampEvidenceHistory.js";
import { StampLearning } from "../components/StampLearning.js";
import { ValidationWarning } from "../components/ValidationWarning.js";

export function StampDetailPage() {
  const { t } = useTranslation();
  const { hash } = useLocation();
  const { stampId = "" } = useParams();
  const { persona } = useAppContext();
  const employeeId = persona.kind === "employee" ? persona.id : "";
  const query = useStampDetail(employeeId, stampId);
  const mutation = usePassportCommand();
  const back = useReturnNavigation(query.data ? `/me/passport/${query.data.page.id}` : "/me/passport");
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    if (hash === "#learning" && query.data)
      document.getElementById("learning")?.scrollIntoView({ block: "start" });
  }, [hash, query.data]);
  if (persona.kind !== "employee") return null;
  const detail = query.data;
  const act = async (command: PassportCommand) => {
    try {
      const result = await mutation.mutateAsync(command);
      setNotice(result.message);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : t("common.error"));
    }
  };
  const completedResources = new Set(
    detail?.evidence.filter((item) => item.type === "learning_completion").map((item) => item.source),
  );
  const submissionGates = detail?.unmetGates.filter((gate) => gate !== "behaviour") ?? [];
  return (
    <>
      <BackLink controlId="passport.stamp.back" to={back.to}>
        {t(back.labelKey)}
      </BackLink>
      <PassportQueryState
        error={query.isError}
        loading={query.isLoading}
        onRetry={() => void query.refetch()}
      >
        {detail ? (
          <>
            <PageHeading
              action={
                <StatusBadge label={t(`status.${detail.progress.status}`)} status={detail.progress.status} />
              }
              eyebrow={detail.page.title}
              title={detail.stamp.title}
              subtitle={detail.stamp.observableResult}
            />
            <PageColumns
              aside={
                <>
                  <StampAtomicCoverage coverage={detail.progress.atomicCoverage}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-medium">{t("passportVig.stamp.evidenceFacets")}</h3>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {t("passportVig.stamp.facetsNote")}
                        </p>
                      </div>
                      <EvidenceFacets progress={detail.progress} />
                    </div>
                  </StampAtomicCoverage>
                  {detail.progress.supervisorFeedback ? (
                    <Card className="bg-warning text-warning-foreground">
                      <CardHeader title={t("passportVig.stamp.feedback")} />
                      <p className="mt-2">“{detail.progress.supervisorFeedback}”</p>
                    </Card>
                  ) : null}
                  <StampEvidenceHistory evidence={detail.evidence} />
                  <ValidationWarning />
                </>
              }
            >
              <AssessmentPassportCard stampId={stampId} pageId={detail.page.id} title={detail.stamp.title} />
              {notice ? (
                <div
                  aria-live="polite"
                  className="flex items-center gap-2 rounded-xl bg-info px-4 py-3 font-medium text-info-foreground"
                >
                  <CheckCircle2 aria-hidden="true" className="size-5" />
                  {notice}
                </div>
              ) : null}
              {detail.progress.status === "established" ? (
                <Card className="bg-success text-success-foreground">
                  <BadgeCheck aria-hidden="true" className="mb-3 size-8" />
                  <CardHeader title={t("passportVig.stamp.confirmed")} />
                </Card>
              ) : detail.progress.status === "on_checklist" ? (
                <Card className="bg-warning text-warning-foreground">
                  <Clock3 aria-hidden="true" className="mb-3 size-8" />
                  <CardHeader title={t("passportVig.stamp.waiting")} />
                  <p className="mt-1 text-sm">{t("passportVig.stamp.waitingDescription")}</p>
                </Card>
              ) : (
                <div className="space-y-5">
                  <StampLearning
                    act={act}
                    criteria={detail.criteria}
                    completedResourceIds={completedResources}
                    employeeId={employeeId}
                    pending={mutation.isPending}
                    resources={detail.learningResources}
                    recommendedResourceIds={new Set(detail.recommendedResourceIds)}
                    stampId={stampId}
                  />
                  <PracticeEvidenceForm
                    act={act}
                    employeeId={employeeId}
                    pending={mutation.isPending}
                    stampId={stampId}
                  />
                  <Card>
                    <CardHeader title={t("passportVig.stamp.submitTitle")} />
                    <p className="mt-2 text-sm text-muted-foreground">
                      {submissionGates.length
                        ? t("passportVig.stamp.submitBlocked", {
                            gates: submissionGates.map((gate) => t(`passportVig.gates.${gate}`)).join(", "),
                          })
                        : t("passportVig.stamp.submitReady")}
                    </p>
                    <Button
                      className="mt-5"
                      controlId="passport.stamp.submit-review"
                      disabled={mutation.isPending || submissionGates.length > 0}
                      onClick={() => void act({ type: "stamp_submitted_for_review", employeeId, stampId })}
                    >
                      {t("passportVig.actions.submitReview")}
                    </Button>
                  </Card>
                </div>
              )}
              {detail.progress.status === "on_checklist" || detail.progress.status === "established" ? (
                <StampLearning
                  act={act}
                  criteria={detail.criteria}
                  completedResourceIds={completedResources}
                  employeeId={employeeId}
                  pending={false}
                  readOnly
                  resources={detail.learningResources}
                  recommendedResourceIds={new Set(detail.recommendedResourceIds)}
                  stampId={stampId}
                />
              ) : null}
              <StampAdvancedMatrix detail={detail} />
            </PageColumns>
          </>
        ) : null}
      </PassportQueryState>
    </>
  );
}
